import * as vp from 'vopay';
import {
  Box,
  Card,
  Title,
  Pagination,
  Icon,
  formattedCurrency,
  usePagination,
  useUpdateQueryParams,
  GetColorStatus,
  capitalizeFirstLetter,
} from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import useFetchAccountTransferSchedules from '@/hooks/useApi/useFetchAccountTransferSchedules';
import ScheduledTransactionsForm from './ScheduledTransactionsForm';
import styles from './ScheduledTransactions.module.scss';

import { TableFiltersContainer, TransactionStatus, Table } from 'UI';

import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useTranslation } from 'react-i18next';
import { useI18nLabels } from '@/utils/useI18nLabels';

type QueryParams = {
  transferType: string;
  frequency: string;
  status: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
};

type ScheduledClientAccountTransactionSearchParams = {
  transferType?: string;
  frequency?: string;
  status?: string;
  searchTerm?: string;
  timeRange?: [vp.Timestamp, vp.Timestamp];
};

type FC = FunctionComponent;

const ScheduledTransactionsContainer: FC = () => {
  const location = useLocation();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const transferType = useMemo(() => queryParams.get('transferType') || '', [queryParams]);
  const status = useMemo(() => queryParams.get('status') || '', [queryParams]);
  const frequency = useMemo(() => queryParams.get('frequency') || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);
  const startDate = useMemo(() => queryParams.get('startDate') || '', [queryParams]);
  const endDate = useMemo(() => queryParams.get('endDate') || '', [queryParams]);
  const timeDifference = moment().subtract(1, 'year').utc().format('YYYY-MM-DD');
  const currentDateTime = moment().utc().format('YYYY-MM-DD');

  const filtersApplied = useMemo(() => {
    return Boolean(
      transferType ||
        status ||
        frequency ||
        searchTerm ||
        (startDate && startDate !== timeDifference) ||
        (endDate && endDate !== currentDateTime),
    );
  }, [transferType, status, frequency, searchTerm, startDate, endDate, timeDifference, currentDateTime]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const [initialLoad, setInitialLoad] = useState(false);
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [filteredList, setFilteredList] = useState<vp.AccountTransferSchedule[]>([]);
  const [scheduledTransactionsResponse, , , fetchScheduledTransactions] = useFetchAccountTransferSchedules({});
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts.Schedule' });
  const { frequencyLabelMapper, transferTypeLabelMapper } = useI18nLabels();
  const alertDialog = useAlertDialog();

  // pagination
  const totalDataLength = useMemo(() => {
    return scheduledTransactionsResponse.PaginationMetaData?.TotalRecords || 0;
  }, [scheduledTransactionsResponse.ScheduledClientAccountTransfers]);
  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const search = async (offset = 0, searchParams: ScheduledClientAccountTransactionSearchParams = {}) => {
    const defaultValues = {
      StartDate: moment(searchParams.timeRange?.[0] ?? (startDate || timeDifference)).format('YYYY-MM-DD'),
      EndDate: moment(searchParams.timeRange?.[1] ?? (endDate || currentDateTime)).format('YYYY-MM-DD'),
      ...(searchParams.transferType === 'all' ? {} : { TransferType: searchParams.transferType }),
      ...(searchParams.frequency === 'all' ? {} : { TransferFrequency: searchParams.frequency }),
      ...(searchParams.status === 'all' ? {} : { TransferStatus: searchParams.status }),
      SearchText: searchParams.searchTerm ?? searchTerm,
      Limit: PER_PAGE,
      Offset: offset,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);
      return;
    }

    try {
      const res = await fetchScheduledTransactions(defaultValues);

      if (!res.Success) throw new Error(res.ErrorMessage);
      const scheduledTransactionRequest = Object.values(
        (res.ScheduledClientAccountTransfers as { [key: number]: vp.AccountTransferSchedule }) || {},
      );
      setFilteredList(scheduledTransactionRequest);
      setInitialLoad(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('FetchScheduledTransactionsError.defaultMessage');
      alertDialog.error(t('FetchScheduledTransactionsError.title'), msg);
      console.error(err);
    }
  };

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [endDate]);

  useEffect(() => {
    const offset = setPageIndex(0);
    const searchParams = {
      transferType,
      status,
      searchTerm,
      frequency,
      startDate: moment(startDate || timeDifference).format('YYYY-MM-DD'),
      endDate: moment(endDate || currentDateTime).format('YYYY-MM-DD'),
    };
    updateQueryParams(searchParams);
    search(offset, searchParams);
  }, []);

  useEffect(() => {
    const ScheduledTransactionsRequest = Object.values(
      (scheduledTransactionsResponse.ScheduledClientAccountTransfers as {
        [key: number]: vp.AccountTransferSchedule;
      }) || {},
    ).reverse();
    setFilteredList(ScheduledTransactionsRequest);
  }, [scheduledTransactionsResponse.ScheduledClientAccountTransfers]);

  const tableProps: vp.TableProps<vp.AccountTransferSchedule> = {
    id: 'scheduled-transactions-list',
    version: '2',
    columns: [
      {
        id: 'id',
        header: t('Table.id'),
        align: 'left',
        template: ({ data }) => (
          <Table.Link
            to={{
              pathname: `/client-account-schedule/details/${data.ID}`,
            }}
          >
            {data.ID}
          </Table.Link>
        ),
      },
      {
        id: 'debitorClientAccountID',
        header: t('Table.debitorClientAccountID'),
        align: 'left',
        field: 'DebitorClientAccountID',
      },
      {
        id: 'recipientClientAccountID',
        header: t('Table.recipientClientAccountID'),
        align: 'left',
        template: ({ data }) =>
          data.RecipientClientAccountID ??
          (JSON.parse(data.RecipientClientAccountIDSplit) as { ClientAccountID: string }[])
            .map((item) => item.ClientAccountID)
            .join(', '),
      },
      {
        id: 'transferType',
        header: t('Table.transferType'),
        align: 'left',
        template: ({ data }) => transferTypeLabelMapper(data.TransferType),
      },
      {
        id: 'frequency',
        header: t('Table.frequency'),
        align: 'left',
        template: ({ data }) => frequencyLabelMapper(data.Frequency),
      },
      {
        id: 'amount',
        header: t('Table.amount'),
        align: 'left',
        template: ({ data }) => `$ ${formattedCurrency(data.Amount.toString())} ${data.Currency}`,
      },
      {
        id: 'startDate',
        header: t('Table.startDate'),
        align: 'left',
        field: 'ScheduleStartDate',
      },
      {
        id: 'ending',
        header: t('Table.ending'),
        align: 'left',
        template: ({ data }) =>
          data.EndingAfterPayments
            ? t('Table.Row.ending', { number: data.EndingAfterPayments })
            : data.ScheduleEndDate || '',
      },
      {
        id: 'status',
        header: t('Table.status'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus status={GetColorStatus(data.Status)} label={capitalizeFirstLetter(data.Status) || ''} />
          </Box>
        ),
      },
    ],
    rows: filteredList.map((item, index) => ({
      id: item.ID || String(index),
      data: item,
    })),
  };

  const EmptyOrNoResults = () => {
    const isEmpty = initialLoad && tableProps.rows?.length === 0;
    const isNoSearch = !initialLoad;

    const message = isNoSearch ? t('Table.Messages.useSearchTool') : t('Table.Messages.noRecordsToDisplay');

    return isEmpty || isNoSearch ? (
      <Box flx="col" className={styles.transactionTableContainer} width="100%" gap="common">
        <Box flx="col" jc="center" gap="sm">
          {isNoSearch && (
            <Box ai="center" jc="center" height="auto">
              <Icon icon="search2" size="lg" />
            </Box>
          )}
          <Box ai="center" jc="center" pt="md" height="auto">
            {message}
          </Box>
        </Box>
      </Box>
    ) : null;
  };

  return (
    <>
      <Box flx="row" jc="center">
        <Box flx="col" gapSize="36px" width="100%">
          <Title title={t('title')} />
          <Card
            style={{
              padding: '16px',
            }}
          >
            <Box flx="col" gap="sm">
              <TableFiltersContainer filtersApplied={filtersApplied}>
                <TableFiltersContainer.Trigger />
                <TableFiltersContainer.Content>
                  <ScheduledTransactionsForm
                    transferType={transferType}
                    frequency={frequency}
                    status={status}
                    searchTerm={searchTerm}
                    hasInvalidEndDate={hasInvalidEndDate}
                    shouldShowEndDateError={shouldShowEndDateError}
                    setShouldShowEndDateError={setShouldShowEndDateError}
                    initialTimeRange={[
                      moment(startDate || timeDifference).valueOf() as vp.Timestamp,
                      moment(endDate || currentDateTime).valueOf() as vp.Timestamp,
                    ]}
                    onSearch={(searchParams) => {
                      const offset = setPageIndex(0);
                      updateQueryParams({
                        transferType: searchParams.transferType || '',
                        frequency: searchParams.frequency || '',
                        searchTerm: searchParams.searchTerm || '',
                        startDate: moment(searchParams.timeRange?.[0]).format('YYYY-MM-DD'),
                        endDate: moment(searchParams.timeRange?.[1]).format('YYYY-MM-DD'),
                        status: searchParams.status || '',
                      });
                      search(offset, searchParams);
                    }}
                  />
                </TableFiltersContainer.Content>
              </TableFiltersContainer>
            </Box>
            <Box>
              <Table {...tableProps} />
            </Box>
            <EmptyOrNoResults />
            <Box width="100%" jc="center" mt="lg" mb="sm">
              {pageCount > 1 && (
                <Pagination
                  pageCount={pageCount}
                  forcePageNumber={pageIndex}
                  handlePageClick={(e) => {
                    const offset = setPageIndex(e.selected);
                    const searchParams = {
                      transferType: transferType,
                      frequency: frequency,
                      searchTerm: searchTerm,
                      startDate: moment(startDate || timeDifference).format('YYYY-MM-DD'),
                      endDate: moment(endDate || currentDateTime).format('YYYY-MM-DD'),
                      status: status,
                    };
                    search(offset, searchParams);
                  }}
                />
              )}
            </Box>
          </Card>
        </Box>
      </Box>
    </>
  );
};

export default ScheduledTransactionsContainer;
